import type { QueryClient } from '@tanstack/react-query'

import { marketEndpoints } from '@/api/endpointCache'
import type { HeatmapSizing } from '@/features/market/api/marketApi'
import {
  backendSectorKey,
  displayDate,
  marketStateIsOpen,
  sectorSlug,
} from '@/features/market/api/marketMappers'
import { formatPercent } from '@/lib/format'
import { toChartNumber, type Money } from '@/lib/money'
import {
  heatmapSchema,
  sectorHeatmapTileSchema,
  stockHeatmapTileSchema,
  type Heatmap,
  type HeatmapQuery,
  type HeatSizing,
  type HeatTile,
  type SectorHeatmapTile,
  type StockHeatmapTile,
} from '@/schemas/heatmap'

/**
 * The chip's value in mm-be's words. mm-be sizes the tiles itself — `size` on
 * every row is already the chosen figure — so the chip is a request parameter
 * and each sizing is its own response.
 *
 * Sent even for market cap, mm-be's default, so the choice is visible here
 * rather than inherited from a server default that could change.
 */
const SIZING_PARAM: Readonly<Record<HeatSizing, HeatmapSizing>> = {
  cap: 'market_cap',
  turnover: 'turnover',
  volume: 'volume',
}

const SIZING_WORDS: Readonly<Record<HeatSizing, string>> = {
  cap: 'market cap',
  turnover: 'turnover',
  volume: 'volume',
}

const asNumber = (value: Money | null): number | null =>
  value === null ? null : toChartNumber(value)

const sectorTile = (row: SectorHeatmapTile): HeatTile => ({
  id: sectorSlug(row.key),
  name: row.name,
  percentChange: asNumber(row.change_percent),
  size: asNumber(row.size),
  nameCount: row.name_count,
  ...(row.market_cap_share === null ? {} : { marketCapShare: toChartNumber(row.market_cap_share) }),
})

const nameTile = (row: StockHeatmapTile): HeatTile => ({
  id: row.key,
  name: row.name,
  percentChange: toChartNumber(row.change_percent),
  size: asNumber(row.size),
})

const count = (n: number, one: string, many: string): string =>
  `${String(n)} ${n === 1 ? one : many}`

/**
 * Whole percent, as the frame prints it ("34% of market cap"), except that a
 * sector holding a sliver of the market reads "<1%" rather than a false "0%".
 */
const marketShare = (share: Money): string => {
  const value = toChartNumber(share)
  return value > 0 && value < 1
    ? '<1% of market cap'
    : `${String(Math.round(value))}% of market cap`
}

/**
 * The drill-in caption, V2 `4243:118584`: "19 names · 34% of market cap ·
 * −0.15% today".
 *
 * Read off the sector's own tile one level up, never recomputed from its
 * names: under the Turnover or Volume chip a name carries no market cap to sum
 * or weigh by, and mm-be publishes `name_count`, `market_cap_share` and the
 * market-cap-weighted move on the sector tile for exactly this line. It also
 * keeps the figure inside equal to the tile the member pressed. A part mm-be
 * has no figure for is left out rather than printed as zero.
 */
const sectorCaption = (sector: SectorHeatmapTile | undefined, names: number): string => {
  if (sector === undefined) return count(names, 'name', 'names')
  return [
    count(sector.name_count, 'name', 'names'),
    sector.market_cap_share === null ? null : marketShare(sector.market_cap_share),
    sector.change_percent === null ? null : `${formatPercent(sector.change_percent)} today`,
  ]
    .filter((part) => part !== null)
    .join(' · ')
}

/**
 * The line that says what a tile with no figure is doing on the map.
 *
 * `Treemap` draws such a tile at its smallest size rather than dropping the
 * name, so the map has to say that size is not a reading — and when no tile
 * has a figure, that every tile is drawn the same size and the sizing tells
 * nothing.
 */
const unsizedNote = (tiles: readonly HeatTile[], unit: string, word: string): string | null => {
  const unsized = tiles.filter((tile) => tile.size === null)
  const [only] = unsized
  if (only === undefined) return null
  if (tiles.length === 1) return `${only.name} has no ${word} on record.`
  if (unsized.length === tiles.length) {
    return `No ${unit} here has a ${word} on record, so every tile is drawn the same size.`
  }
  return unsized.length === 1
    ? `${only.name} has no ${word} on record, so it is drawn smallest.`
    : `${count(unsized.length, unit, `${unit}s`)} have no ${word} on record, so they are drawn smallest.`
}

const sourceLine = (query: HeatmapQuery, session: string, tiles: readonly HeatTile[]): string => {
  const word = SIZING_WORDS[query.sizing]
  const sizing =
    query.sector !== undefined
      ? `Tile size is each name's ${word} inside the group.`
      : query.sizing === 'cap'
        ? 'Size shows how big the group is, not how much it moved.'
        : `Size shows the group's ${word} today, not how much it moved.`
  const note = unsizedNote(tiles, query.sector === undefined ? 'sector' : 'name', word)
  return [`Source: NEPSE · ${session}.`, sizing, note].filter((part) => part !== null).join(' ')
}

export const heatmapService = {
  heatmap: async (query: HeatmapQuery, client?: QueryClient): Promise<Heatmap> => {
    const api = marketEndpoints(client)
    const sizing = SIZING_PARAM[query.sizing]
    // Every request is an endpoint-cache entry, and none of them is new to a
    // drill-in: the sector level is the map the member pressed a tile on,
    // asked with the same arguments, and `summary` and `status` are the ones
    // the rail and the top nav already hold. A drill-in usually costs the one
    // request for its names.
    const [names, sectors, summary, status] = await Promise.all([
      query.sector === undefined
        ? Promise.resolve(null)
        : api.heatmap(backendSectorKey(query.sector), sizing),
      api.heatmap(undefined, sizing),
      api.summary(),
      api.status(),
    ])

    const asOf = displayDate(summary.as_of ?? status.as_of)
    const session = marketStateIsOpen(status.state) ? asOf : `${asOf} close`
    const sectorRows = sectorHeatmapTileSchema.array().parse(sectors.rows)

    if (query.sector === undefined || names === null) {
      const tiles = sectorRows.map(sectorTile)
      return heatmapSchema.parse({
        tiles,
        caption: 'Tap a sector to open its names',
        source: sourceLine(query, session, tiles),
      })
    }

    // The same key the request went out with: mm-be matches it `iexact`, and
    // spells its own keys upper-case.
    const key = backendSectorKey(query.sector)
    const nameRows = stockHeatmapTileSchema.array().parse(names.rows)
    const sector = sectorRows.find((row) => row.key.toUpperCase() === key)
    const tiles = nameRows.map(nameTile)
    const sectorName = sector?.name ?? nameRows[0]?.sector_name ?? null

    return heatmapSchema.parse({
      tiles,
      ...(sectorName === null ? {} : { sectorName }),
      caption: sectorCaption(sector, nameRows.length),
      source: sourceLine(query, session, tiles),
    })
  },
}
